import React from 'react';
import AppImage from '@/components/ui/AppImage';
import { useImageProtection, ImageProtectionOptions } from './useImageProtection';

/**
 * Props for the ProtectedAppImage component
 * Extends AppImage props with protection options
 */
export interface ProtectedAppImageProps extends React.ComponentProps<typeof AppImage> {
  /** Whether to show a message when protection is triggered */
  showMessage?: boolean;
  /** Custom message to display when protection is triggered */
  customMessage?: string;
  /** Callback invoked when a user attempts to access the context menu or drag the image */
  onProtectionAttempt?: () => void;
}

/**
 * ProtectedAppImage component
 * 
 * A wrapper component for AppImage that prevents right-click context menu
 * and drag-and-drop while maintaining all AppImage functionality including
 * error handling, loading states, and automatic fallback.
 * 
 * @example
 * ```tsx
 * <ProtectedAppImage
 *   src="https://example.com/image.jpg"
 *   alt="Gallery photo"
 *   className="gallery-img"
 * />
 * ```
 */
export function ProtectedAppImage({
  showMessage,
  customMessage,
  onProtectionAttempt,
  ...appImageProps
}: ProtectedAppImageProps) {
  // Get protection handlers and styles from the hook
  const protectionOptions: ImageProtectionOptions = {
    showMessage,
    customMessage,
    onAttempt: onProtectionAttempt,
  };

  const { onContextMenu, onDragStart, style: protectionStyle } = useImageProtection(protectionOptions);

  return (
    <div
      onContextMenu={onContextMenu}
      onDragStart={onDragStart}
      style={protectionStyle}
      className="inline-block w-full h-full"
    >
      <AppImage {...appImageProps} />
    </div>
  );
}
